import React, { createContext, useContext, useState, useEffect } from 'react';
import { whiteLabelStore, WhiteLabelConfig, DEFAULT_CONFIG } from '../utils/whiteLabel';
import { isFeatureEnabled } from '../config/environment';

interface WhiteLabelContextType {
  config: WhiteLabelConfig;
  updateConfig: (config: WhiteLabelConfig) => void;
  resetConfig: () => void;
  applyTheme: (config: WhiteLabelConfig) => void;
}

const WhiteLabelContext = createContext<WhiteLabelContextType | undefined>(undefined);

export const WhiteLabelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<WhiteLabelConfig>(whiteLabelStore.get());

  // Apply theme to CSS variables and inject dynamic styles
  const applyTheme = (cfg: WhiteLabelConfig) => {
    const root = document.documentElement;
    root.style.setProperty('--wl-primary', cfg.primaryColor);
    root.style.setProperty('--wl-secondary', cfg.secondaryColor);
    root.style.setProperty('--wl-client-name', `"${cfg.clientName}"`);

    // Only apply color overrides if feature is enabled and not using defaults
    if (!isFeatureEnabled('whiteLabel')) return;

    // Create or update dynamic style element
    let styleEl = document.getElementById('white-label-styles') as HTMLStyleElement;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'white-label-styles';
      document.head.appendChild(styleEl);
    }

    // Generate CSS to override Tailwind's primary and secondary colors
    // This allows existing Tailwind classes to use the white-label colors
    const isDefault = cfg.primaryColor === DEFAULT_CONFIG.primaryColor &&
                      cfg.secondaryColor === DEFAULT_CONFIG.secondaryColor;

    if (isDefault) {
      // Remove overrides when using defaults
      styleEl.textContent = '';
    } else {
      // Inject CSS overrides for Tailwind color classes
      styleEl.textContent = `
        /* White-label color overrides */
        :root {
          --color-primary: ${cfg.primaryColor};
          --color-secondary: ${cfg.secondaryColor};
        }

        /* Override Tailwind primary colors */
        .bg-primary-500, .bg-primary { background-color: ${cfg.primaryColor} !important; }
        .bg-primary-600 { background-color: ${adjustColor(cfg.primaryColor, -10)} !important; }
        .bg-primary-400 { background-color: ${adjustColor(cfg.primaryColor, 10)} !important; }
        .text-primary-500, .text-primary { color: ${cfg.primaryColor} !important; }
        .border-primary-500, .border-primary { border-color: ${cfg.primaryColor} !important; }
        .ring-primary-500 { --tw-ring-color: ${cfg.primaryColor} !important; }

        /* Override Tailwind secondary colors */
        .bg-secondary-800, .bg-secondary { background-color: ${cfg.secondaryColor} !important; }
        .bg-secondary-900 { background-color: ${adjustColor(cfg.secondaryColor, -10)} !important; }
        .text-secondary, .text-night { color: ${cfg.secondaryColor} !important; }

        /* Seafoam/Teal overrides (primary color alias) */
        .bg-seafoam { background-color: ${cfg.primaryColor} !important; }
        .bg-seafoam-600, .hover\\:bg-seafoam-600:hover { background-color: ${adjustColor(cfg.primaryColor, -10)} !important; }
        .bg-teal { background-color: ${cfg.primaryColor} !important; }
        .text-seafoam, .text-teal { color: ${cfg.primaryColor} !important; }

        /* Forest/Moss overrides (used in sidebar and gradients) */
        .bg-forest { background-color: ${cfg.secondaryColor} !important; }
        .bg-moss { background-color: ${cfg.secondaryColor} !important; }
        .from-moss { --tw-gradient-from: ${cfg.secondaryColor} !important; }
        .to-night { --tw-gradient-to: ${cfg.secondaryColor} !important; }

        /* Brand gradient overrides (page headers) */
        .bg-brand-gradient, .bg-gradient-primary {
          background: linear-gradient(90deg, ${cfg.secondaryColor} 0%, ${adjustColor(cfg.secondaryColor, -15)} 100%) !important;
        }

        /* Green button overrides (action buttons) */
        .bg-green-500 { background-color: ${cfg.primaryColor} !important; }
        .bg-green-600, .hover\\:bg-green-600:hover { background-color: ${adjustColor(cfg.primaryColor, -10)} !important; }

        /* Focus ring overrides */
        .focus\\:ring-seafoam:focus { --tw-ring-color: ${cfg.primaryColor} !important; }
        .focus\\:border-seafoam:focus { border-color: ${cfg.primaryColor} !important; }

        /* Sky color overrides (used in some badges/buttons) */
        .bg-sky-100 { background-color: ${adjustColor(cfg.primaryColor, 40)} !important; }
        .bg-sky-200, .hover\\:bg-sky-200:hover { background-color: ${adjustColor(cfg.primaryColor, 30)} !important; }
        .text-sky-800 { color: ${adjustColor(cfg.primaryColor, -30)} !important; }
      `;
    }
  };

  // Helper function to adjust color brightness
  const adjustColor = (color: string, percent: number): string => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    ).toString(16).slice(1);
  };

  // Load config on mount
  useEffect(() => {
    const cfg = whiteLabelStore.get();
    setConfig(cfg);
    applyTheme(cfg);
  }, []);

  // Listen for storage changes (cross-tab sync)
  useEffect(() => {
    const handleUpdate = () => {
      const cfg = whiteLabelStore.get();
      setConfig(cfg);
      applyTheme(cfg);
    };

    window.addEventListener('whitelabel-update', handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('whitelabel-update', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, []);

  const updateConfig = (newConfig: WhiteLabelConfig) => {
    whiteLabelStore.save(newConfig);
    setConfig(newConfig);
    applyTheme(newConfig);
  };

  const resetConfig = () => {
    whiteLabelStore.reset();
    const cfg = DEFAULT_CONFIG;
    setConfig(cfg);
    applyTheme(cfg);
  };

  return (
    <WhiteLabelContext.Provider value={{ config, updateConfig, resetConfig, applyTheme }}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

export const useWhiteLabel = (): WhiteLabelContextType => {
  const context = useContext(WhiteLabelContext);

  if (context === undefined) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }

  return context;
};

export default WhiteLabelContext;
